import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { BarChart3, Building2, Download, Loader } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const chartRef = useRef(null);

  useEffect(() => {
    fetchCaseVolumeReport();
  }, []);

  const fetchCaseVolumeReport = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/case-volume-per-facility');
      setReportData(response.data.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData) return;

    const headers = ['Facility', 'Total Cases', ...reportData.caseTypes];
    const rows = [headers.join(',')];

    reportData.facilities.forEach(facility => {
      const row = [
        `"${facility.facilityName}"`,
        facility.totalCases,
        ...reportData.caseTypes.map(type => facility.breakdown[type] || 0)
      ];
      rows.push(row.join(','));
    });

    // Add totals row
    const totalsRow = [
      '"TOTAL"',
      reportData.totals.totalCases,
      ...reportData.caseTypes.map(type => reportData.totals.byType[type] || 0)
    ];
    rows.push(totalsRow.join(','));

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `case-volume-per-facility-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Report exported to CSV');
  };

  const getCaseTypeColor = (caseType) => {
    const colors = {
      'Stolen Pins': 'bg-blue-100 text-blue-800',
      'Suspicious Payments': 'bg-yellow-100 text-yellow-800',
      'Contraband': 'bg-red-100 text-red-800',
      'Escape': 'bg-purple-100 text-purple-800',
      'Chargebacks': 'bg-orange-100 text-orange-800',
      'Staff (restricted)': 'bg-pink-100 text-pink-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[caseType] || 'bg-gray-100 text-gray-800';
  };

  const getCaseTypeChartColor = (caseType) => {
    const colors = {
      'Stolen Pins': '#3B82F6',
      'Suspicious Payments': '#FBBF24',
      'Contraband': '#EF4444',
      'Escape': '#A855F7',
      'Chargebacks': '#F97316',
      'Staff (restricted)': '#EC4899',
      'Other': '#6B7280'
    };
    return colors[caseType] || '#6B7280';
  };

  const getHighchartsOptions = () => {
    if (!reportData || !reportData.facilities || reportData.facilities.length === 0) {
      return null;
    }

    // Prepare data for stacked column chart
    const categories = reportData.facilities.map(f => f.facilityName);
    const series = reportData.caseTypes.map(caseType => ({
      name: caseType,
      data: reportData.facilities.map(facility => facility.breakdown[caseType] || 0),
      color: getCaseTypeChartColor(caseType)
    }));

    return {
      chart: {
        type: 'column',
        height: 500,
      },
      title: {
        text: 'Case Volume per Facility by Case Type',
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
        },
      },
      xAxis: {
        categories: categories,
        title: {
          text: 'Facility',
        },
        labels: {
          rotation: -45,
          align: 'right',
          style: {
            fontSize: '11px',
          },
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Number of Cases',
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function() {
          let tooltip = '<b>' + this.x + '</b><br/>';
          let total = 0;
          this.points.forEach(point => {
            if (point.y > 0) {
              tooltip += '<span style="color:' + point.color + '">\u25CF</span> ' + 
                        point.series.name + ': <b>' + point.y + '</b><br/>';
              total += point.y;
            }
          });
          tooltip += '<b>Total: ' + total + '</b>';
          return tooltip;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      series: series,
      credits: {
        enabled: false,
      },
    };
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading report...</span>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">No report data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Case Volume per Facility</h1>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Total Facilities</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{reportData.facilities.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Total Cases</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{reportData.totals.totalCases}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Top Case Type</span>
          </div>
          <p className="text-lg font-semibold text-gray-900 mt-2">
            {Object.entries(reportData.totals.byType)
              .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Avg Cases/Facility</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {reportData.facilities.length > 0
              ? Math.round(reportData.totals.totalCases / reportData.facilities.length)
              : 0}
          </p>
        </div>
      </div>

      {/* Highcharts Graph */}
      {reportData && reportData.facilities && reportData.facilities.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={getHighchartsOptions()}
          />
        </div>
      )}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                  Facility
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  Total
                </th>
                {reportData.caseTypes.map((type) => (
                  <th
                    key={type}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {type}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.facilities.map((facility, idx) => (
                <tr key={facility.facilityId || idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {facility.facilityName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-sm font-semibold text-gray-900">
                      {facility.totalCases}
                    </span>
                  </td>
                  {reportData.caseTypes.map((type) => {
                    const count = facility.breakdown[type] || 0;
                    return (
                      <td key={type} className="px-4 py-4 whitespace-nowrap text-center">
                        {count > 0 ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCaseTypeColor(type)}`}>
                            {count}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-gray-50 z-10">
                  <span className="text-sm font-bold text-gray-900">TOTAL</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span className="text-sm font-bold text-gray-900">
                    {reportData.totals.totalCases}
                  </span>
                </td>
                {reportData.caseTypes.map((type) => {
                  const total = reportData.totals.byType[type] || 0;
                  return (
                    <td key={type} className="px-4 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-bold text-gray-900">{total}</span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Case Type Breakdown Chart */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Case Type Distribution</h2>
        <div className="space-y-3">
          {reportData.caseTypes.map((type) => {
            const total = reportData.totals.byType[type] || 0;
            const percentage = reportData.totals.totalCases > 0
              ? (total / reportData.totals.totalCases) * 100
              : 0;
            
            return (
              <div key={type}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{type}</span>
                  <span className="text-sm text-gray-600">
                    {total} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getCaseTypeColor(type).split(' ')[0]}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Reports;
